import { useRef, useCallback } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useUIStore } from "../stores/uiStore";
import { useLayoutStore } from "../stores/layoutStore";

const _plane    = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _raycaster = new THREE.Raycaster();
const _mouse    = new THREE.Vector2();
const _hitPoint = new THREE.Vector3();

interface Props {
  children: React.ReactNode;
}

export function EditableWarehouseGroup({ children }: Props) {
  const editMode      = useUIStore((s) => s.editMode);
  const transform     = useLayoutStore((s) => s.pendingTransform ?? s.transform);
  const updatePending = useLayoutStore((s) => s.updatePending);
  const { camera, gl } = useThree();
  const isDragging    = useRef(false);
  const offsetRef     = useRef({ x: 0, z: 0 });

  const screenToFloor = useCallback((clientX: number, clientY: number) => {
    const rect = gl.domElement.getBoundingClientRect();
    _mouse.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    _raycaster.setFromCamera(_mouse, camera);
    _raycaster.ray.intersectPlane(_plane, _hitPoint);
    return { x: _hitPoint.x, z: _hitPoint.z };
  }, [camera, gl]);

  return (
    <group
      position={[transform.originOffsetX, 0, transform.originOffsetZ]}
      rotation={[0, transform.rotationYRad, 0]}
      scale={transform.scale}
      onPointerDown={editMode ? (e) => {
        e.stopPropagation();
        isDragging.current = true;
        const hit = screenToFloor(e.nativeEvent.clientX, e.nativeEvent.clientY);
        offsetRef.current = {
          x: transform.originOffsetX - hit.x,
          z: transform.originOffsetZ - hit.z,
        };
      } : undefined}
      onPointerMove={editMode ? (e) => {
        if (!isDragging.current) return;
        const hit = screenToFloor(e.nativeEvent.clientX, e.nativeEvent.clientY);
        updatePending({
          originOffsetX: hit.x + offsetRef.current.x,
          originOffsetZ: hit.z + offsetRef.current.z,
        });
      } : undefined}
      onPointerUp={editMode ? () => { isDragging.current = false; } : undefined}
    >
      {editMode && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[30, -0.01, 20]}>
          <planeGeometry args={[120, 80]} />
          <meshBasicMaterial visible={false} />
        </mesh>
      )}
      {children}
    </group>
  );
}
